import React from 'react';
import { ChevronDown, HelpCircle, Search, Plus, Trash2, Edit3 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { AppConfig, SEOConfig } from '../types';
import { EditableText } from './EditableText';

interface FaqProps {
  theme?: string;
  faqs?: { question: string; answer: string }[];
  seo?: SEOConfig;
  isVisualEditActive?: boolean;
  onUpdateConfig?: (partialConfig: Partial<AppConfig>) => void;
}

export function Faq({ theme, faqs, seo, isVisualEditActive = false, onUpdateConfig }: FaqProps) {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  // Default fallback FAQs if none provided
  const fallbackFaqs = [
    {
      question: t('faq_q1' as any) || "Quelles sont les zones couvertes par le studio ?",
      answer: t('faq_a1' as any) || "Basés au Teich sur le Bassin d'Arcachon, nous nous déplaçons dans toute l'Aquitaine (Bordeaux, Saint-Émilion, Cap Ferret) ainsi que partout en France et à l'international."
    },
    {
      question: t('faq_q2' as any) || "Proposez-vous des formules combinées Photo & Vidéo ?",
      answer: t('faq_a2' as any) || "Oui, notre spécialité absolue est la formule Duo complète. Travailler en couple nous permet d'assurer une harmonie visuelle totale et de synchroniser nos prises de vue."
    },
    {
      question: t('faq_q3' as any) || "Quels sont les délais de livraison de vos reportages ?",
      answer: t('faq_a3' as any) || "Comptez 4 à 6 semaines pour la galerie photo en ligne et 6 à 8 semaines pour le film cinématique HDR avec étalonnage et travail sonore soigné."
    },
    {
      question: t('faq_q4' as any) || "Comment s'organise la préparation avant le jour J ?",
      answer: t('faq_a4' as any) || "Nous organisons plusieurs rendez-vous (au studio au Teich ou en visio) pour étudier votre planning, la lumière et les marées afin de programmer la séance de couple idéale."
    },
    {
      question: t('faq_q5' as any) || "La prise de vue par drone est-elle incluse ?",
      answer: t('faq_a5' as any) || "Oui, Yohann est télépilote professionnel certifié. Les plans aériens par drone de votre domaine ou château sont inclus dans nos formules vidéo et duo."
    }
  ];

  const currentFaqs = (faqs && faqs.length > 0) ? faqs : (seo?.faqs && seo.faqs.length > 0) ? seo.faqs : fallbackFaqs;

  const toggleIndex = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleUpdateFaqItem = (index: number, newQuestion: string, newAnswer: string) => {
    const updated = currentFaqs.map((item, i) => i === index ? { question: newQuestion, answer: newAnswer } : item);
    if (onUpdateConfig) {
      onUpdateConfig({
        seo: {
          metaTitle: seo?.metaTitle || '',
          metaDescription: seo?.metaDescription || '',
          keywords: seo?.keywords || '',
          aiAgentInstructions: seo?.aiAgentInstructions || '',
          faqs: updated
        }
      });
    }
  };

  const handleAddFaqItem = () => {
    const newItems = [
      ...currentFaqs,
      {
        question: "Nouvelle question fréquente ?",
        answer: "Saisissez ici la réponse détaillée d'Aurélie & Yohann pour orienter vos futurs mariés."
      }
    ];
    setOpenIndex(newItems.length - 1);
    if (onUpdateConfig) {
      onUpdateConfig({
        seo: {
          metaTitle: seo?.metaTitle || '',
          metaDescription: seo?.metaDescription || '',
          keywords: seo?.keywords || '',
          aiAgentInstructions: seo?.aiAgentInstructions || '',
          faqs: newItems
        }
      });
    }
  };

  const handleDeleteFaqItem = (index: number) => {
    const updated = currentFaqs.filter((_, i) => i !== index);
    if (onUpdateConfig) {
      onUpdateConfig({
        seo: {
          metaTitle: seo?.metaTitle || '',
          metaDescription: seo?.metaDescription || '',
          keywords: seo?.keywords || '',
          aiAgentInstructions: seo?.aiAgentInstructions || '',
          faqs: updated
        }
      });
    }
  };

  const isSherlock = theme === 'sherlock-wedding';
  const fullText = t('faq_title');
  const chars = fullText.split('');
  
  const [isInView, setIsInView] = React.useState(false);
  const titleRef = React.useRef(null);

  React.useEffect(() => {
    if (!isSherlock) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => observer.disconnect();
  }, [isSherlock]);

  const totalDuration = 1.5;

  return (
    <section className="py-24 bg-theme-bg relative overflow-hidden border-t border-theme-border z-10" id="faq-section">
      {/* Background aesthetic glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[250px] bg-theme-accent/3 blur-[100px] rounded-full pointer-events-none" />

      {isSherlock && (
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes drawSign {
            0% { stroke-dashoffset: 400; opacity: 0; }
            100% { stroke-dashoffset: 0; opacity: 1; }
          }
          .animate-draw-sign {
            stroke-dasharray: 400;
            stroke-dashoffset: 400;
            animation: drawSign 2.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            animation-delay: 2s;
          }
          .sherlock-handwritten-title {
            font-family: var(--font-cursive);
            white-space: pre-wrap;
            display: inline-block;
            position: relative;
          }
        `}} />
      )}

      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 relative">
          {isVisualEditActive && (
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 bg-amber-400/10 border border-amber-400/30 text-amber-400 text-xs font-mono uppercase tracking-wider rounded-full">
              <Edit3 className="w-3.5 h-3.5 animate-pulse" />
              <span>Édition Directe FAQ Active</span>
            </div>
          )}

          <span className="text-xs uppercase tracking-[0.3em] text-theme-accent font-mono block mb-3">
            {isSherlock ? '🔍 Questions Rituelles & Enquêtes' : t('faq_tag')}
          </span>
          
          <div className="inline-block relative" ref={titleRef}>
            {isSherlock ? (
              <div className="relative inline-block px-4">
                {/* Vintage Ink Splash Deco on the left */}
                <div className="absolute -left-8 top-4 w-4 h-4 bg-theme-accent/20 rounded-full blur-[2px] pointer-events-none">
                  <div className="absolute -top-1 -left-2 w-1.5 h-1.5 bg-theme-accent/30 rounded-full" />
                  <div className="absolute top-2 left-3 w-1 h-1 bg-theme-accent/25 rounded-full" />
                </div>

                <h2 className="text-4xl md:text-6xl text-theme-accent font-light tracking-wide mb-2 sherlock-handwritten-title pr-1 min-h-[70px]">
                  {chars.map((char, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                      transition={{ duration: 0.1, delay: (i / chars.length) * totalDuration }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </h2>

                {/* The actual detailed SVG Quill Feather tracking along the text */}
                <motion.div 
                  className="absolute -top-10 bottom-0 pointer-events-none select-none z-10 text-theme-accent origin-bottom-left"
                  initial={{ left: "-2%", opacity: 0 }}
                  animate={
                    isInView
                      ? {
                          left: ["-2%", "100%", "100%"],
                          opacity: [0, 1, 0],
                        }
                      : { left: "-2%", opacity: 0 }
                  }
                  transition={{
                    duration: totalDuration + 0.5,
                    ease: "linear",
                    times: [0, 0.8, 1]
                  }}
                >
                  <motion.div
                    animate={
                      isInView
                        ? {
                            y: [0, -6, 2, -4, 3, -3, 0],
                            rotate: [0, -8, 5, -6, 4, -4, 0],
                          }
                        : { y: 0, rotate: 0 }
                    }
                    transition={{
                      duration: 0.3,
                      repeat: Math.ceil((totalDuration + 0.5) / 0.3),
                      ease: "linear",
                    }}
                  >
                    <svg className="w-16 h-16 drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)] transform -scale-x-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" fill="currentColor" fillOpacity="0.2"/>
                      <line x1="16" y1="8" x2="2" y2="22" strokeWidth="1.5"/>
                      <line x1="17.5" y1="15" x2="9" y2="15"/>
                    </svg>
                  </motion.div>
                </motion.div>
              </div>
            ) : (
              <h2 className="text-3xl md:text-5xl text-theme-text font-serif font-light tracking-wide mb-4">
                {t('faq_title')}
              </h2>
            )}
          </div>

          {/* Underline design: signature dash trace if Sherlock */}
          {isSherlock ? (
            <div className="w-64 h-8 mx-auto -mt-2 overflow-visible relative">
              <svg className="w-full h-full text-theme-accent/60" viewBox="0 0 200 30" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path 
                  className="animate-draw-sign"
                  d="M10 12 C 60 22, 100 2, 150 18 C 170 24, 185 14, 195 8" 
                  strokeLinecap="round"
                />
              </svg>
            </div>
          ) : (
            <div className="w-12 h-[1px] bg-theme-accent/50 mx-auto mt-4" />
          )}
        </div>

        {/* FAQ Accordion List */}
        <div className="space-y-4">
          {currentFaqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const qText = t(faq.question as any) || faq.question;
            const aText = t(faq.answer as any) || faq.answer;

            return (
              <div
                key={index}
                className={`bg-theme-card border transition-all duration-300 ${
                  isVisualEditActive
                    ? 'border-amber-400/40 hover:border-amber-400'
                    : 'border-theme-border/60 hover:border-theme-border'
                }`}
                id={`faq-item-${index}`}
              >
                <div className="w-full px-6 py-5 flex items-center justify-between text-left group">
                  <div className="flex items-start gap-3.5 pr-4 flex-1">
                    {isSherlock ? (
                      <Search className="w-4 h-4 text-theme-accent mt-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <HelpCircle className="w-4 h-4 text-theme-accent mt-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    )}
                    <div className="flex-1">
                      {isVisualEditActive ? (
                        <EditableText
                          value={qText}
                          label={`Question FAQ #${index + 1}`}
                          isEditingMode={true}
                          onSave={(newQ) => handleUpdateFaqItem(index, newQ, aText)}
                        >
                          <span className="text-sm font-medium text-theme-text font-sans tracking-wide">
                            {qText}
                          </span>
                        </EditableText>
                      ) : (
                        <button
                          onClick={() => toggleIndex(index)}
                          className="text-left w-full text-sm font-medium text-theme-text group-hover:text-theme-accent transition-colors duration-300 font-sans tracking-wide cursor-pointer"
                        >
                          {qText}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isVisualEditActive && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFaqItem(index);
                        }}
                        className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors cursor-pointer"
                        title="Supprimer cette question FAQ"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}

                    <button
                      onClick={() => toggleIndex(index)}
                      className="p-1 text-theme-secondary hover:text-theme-text transition-colors cursor-pointer"
                    >
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    </button>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {(isOpen || isVisualEditActive) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-1 text-xs md:text-sm text-theme-secondary leading-relaxed font-light border-t border-theme-border/20 pl-[41px]">
                        {isVisualEditActive ? (
                          <EditableText
                            value={aText}
                            label={`Réponse FAQ #${index + 1}`}
                            isEditingMode={true}
                            multiline={true}
                            onSave={(newA) => handleUpdateFaqItem(index, qText, newA)}
                          >
                            <span>{aText}</span>
                          </EditableText>
                        ) : (
                          <span>{aText}</span>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Add FAQ Button in Live Edit Mode */}
        {isVisualEditActive && (
          <div className="mt-6 text-center">
            <button
              onClick={handleAddFaqItem}
              className="px-4 py-2 bg-amber-400/20 hover:bg-amber-400/30 border border-amber-400/40 text-amber-300 font-mono text-xs uppercase tracking-wider transition-all flex items-center gap-2 mx-auto cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter une Question FAQ</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

